import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ShieldCheck } from "lucide-react";

export default function Vouch() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [relationship, setRelationship] = useState("");
  const [note, setNote] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/vouch/${token}`).then(({ data }) => {
      setInfo(data);
      if (data.status === "confirmed") setDone(true);
    }).catch(() => setError(true));
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/vouch/${token}`, { relationship, note });
      setDone(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-[#121721] border border-white/10 p-8 space-y-5">
        <div className="flex justify-center"><LogoMark size={44} /></div>
        {error ? (
          <p className="text-center text-slate-400">This vouch link is invalid or has expired.</p>
        ) : !info ? (
          <p className="text-center text-slate-500">Loading…</p>
        ) : done ? (
          <div className="text-center space-y-3">
            <CheckCircle2 className="h-14 w-14 text-green-400 mx-auto" />
            <h1 className="text-xl font-bold text-white">Thank you!</h1>
            <p className="text-slate-400 text-sm">You've vouched for <strong>{info.owner_name}</strong>. This boosts their trust score on Hustler's Hall.</p>
          </div>
        ) : (
          <>
            <div className="text-center">
              <ShieldCheck className="h-10 w-10 text-amber-400 mx-auto mb-2" />
              <h1 className="text-xl font-bold text-white">Vouch for {info.owner_name}</h1>
              <p className="text-slate-400 text-sm mt-1">
                {info.owner_name} listed you as a reference on Hustler's Hall. If you know and trust them, vouch below.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">How do you know them?</Label>
              <Input data-testid="vouch-relationship" value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="e.g. Business partner of 3 years" className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">A few words (optional)</Label>
              <Textarea data-testid="vouch-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Why others can trust them…" className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500 resize-none" />
            </div>
            <Button data-testid="vouch-submit" onClick={submit} disabled={submitting} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold h-11 rounded-full">
              {submitting ? "Submitting…" : `I vouch for ${info.owner_name}`}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
